import FaxOutlinedIcon from '@mui/icons-material/FaxOutlined';
import { Box, FormControl, FormHelperText, InputLabel, OutlinedInput, Tooltip, Typography } from '@mui/material';
import { Appointment, Encounter } from 'fhir/r4b';
import { enqueueSnackbar } from 'notistack';
import { phone } from 'phone';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputMask from 'src/components/InputMask';
import { dataTestIds } from 'src/constants/data-test-ids';
import { getInPersonVisitStatus, isPhoneNumberValid, TelemedAppointmentStatusEnum } from 'utils';
import { ConfirmationDialog } from '../../../../../components/ConfirmationDialog';
import { RoundedButton } from '../../../../../components/RoundedButton';
import { useGetAppointmentAccessibility } from '../../hooks/useGetAppointmentAccessibility';
import { useOystehrAPIClient } from '../../hooks/useOystehrAPIClient';

interface SendFaxButtonProps {
  appointment?: Appointment;
  encounter?: Encounter;
  inPerson: boolean;
}

export const SendFaxButton: FC<SendFaxButtonProps> = ({ appointment, encounter, inPerson }: SendFaxButtonProps) => {
  const { t } = useTranslation();
  const apiClient = useOystehrAPIClient();
  const [openTooltip, setOpenTooltip] = useState(false);

  const inPersonStatus = useMemo(
    () => appointment && encounter && getInPersonVisitStatus(appointment, encounter),
    [appointment, encounter]
  );
  const appointmentAccessibility = useGetAppointmentAccessibility();

  const [faxNumber, setFaxNumber] = useState('');
  const [faxError, setFaxError] = useState(false);

  const errorMessage = useMemo(() => {
    if (
      appointmentAccessibility.visitType === 'follow-up'
        ? encounter?.status === 'in-progress'
        : (inPerson && inPersonStatus && !['intake', 'completed'].includes(inPersonStatus)) ||
          appointmentAccessibility.status !== TelemedAppointmentStatusEnum.complete
    ) {
      return t('telemed.review.fax.tooltip');
    }
    return null;
  }, [
    appointmentAccessibility.visitType,
    appointmentAccessibility.status,
    encounter?.status,
    inPerson,
    inPersonStatus,
    t,
  ]);

  const handleSendFax = async (): Promise<void> => {
    if (faxError) {
      enqueueSnackbar(t('telemed.review.fax.errorValid'), { variant: 'error' });
      return;
    }

    if (!apiClient || !appointment?.id) {
      throw new Error('api client not defined or appointment ID is missing');
    }

    try {
      await apiClient.sendFax({
        appointmentId: appointment.id,
        faxNumber,
      });
      enqueueSnackbar(t('telemed.review.fax.success'), { variant: 'success' });
    } catch (error: any) {
      console.error('Error sending fax:', error);
      enqueueSnackbar(t('telemed.review.fax.error'), { variant: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'end' }}>
      <Tooltip
        placement="top"
        open={openTooltip && errorMessage !== null}
        onClose={() => setOpenTooltip(false)}
        onOpen={() => setOpenTooltip(true)}
        title={<Typography>{errorMessage !== null && errorMessage}</Typography>}
      >
        <Box>
          <ConfirmationDialog
            title={t('telemed.review.fax.title')}
            description={
              <FormControl variant="outlined" fullWidth error={faxError} sx={{ mt: 2, mb: -2 }}>
                <InputLabel shrink required htmlFor="fax-number">
                  {t('telemed.review.fax.label')}
                </InputLabel>
                <OutlinedInput
                  id="fax-number"
                  label={t('telemed.review.fax.label')}
                  notched
                  required
                  type="tel"
                  placeholder={t('telemed.review.fax.placeholder')}
                  value={faxNumber}
                  inputMode="numeric"
                  inputComponent={InputMask as any}
                  inputProps={{
                    mask: '(000) 000-0000',
                  }}
                  onChange={(e) => {
                    const number = e.target.value.replace(/\D/g, '');
                    setFaxNumber(number);
                    if (isPhoneNumberValid(number) && phone(number).isValid) {
                      setFaxError(false);
                    } else {
                      setFaxError(true);
                    }
                  }}
                />
                <FormHelperText error sx={{ visibility: faxError ? 'visible' : 'hidden' }}>
                  {t('telemed.review.fax.helper')}
                </FormHelperText>
              </FormControl>
            }
            response={handleSendFax}
            actionButtons={{
              proceed: {
                text: t('telemed.review.fax.send'),
                disabled: faxNumber === '' || faxError,
              },
              back: { text: t('telemed.review.fax.cancel') },
              reverse: true,
            }}
          >
            {(showDialog) => (
              <RoundedButton
                disabled={errorMessage !== null}
                variant="outlined"
                onClick={showDialog}
                startIcon={<FaxOutlinedIcon color="inherit" />}
                data-testid={dataTestIds.progressNotePage.sendFaxButton}
              >
                {t('telemed.review.fax.button')}
              </RoundedButton>
            )}
          </ConfirmationDialog>
        </Box>
      </Tooltip>
    </Box>
  );
};
